import type { TextElement, TextMeasure } from '@/lib/design/types'

import { fontCss, getFont, nearestWeight } from '@/lib/design/fonts'

export type TextRun = {
    text: string
    highlight: boolean
}

export type LaidRun = TextRun & {
    width: number
}

export type LaidLine = {
    runs: Array<LaidRun>
    width: number
}

export type TextLayout = {
    lines: Array<LaidLine>
    lineHeight: number
    height: number
}

// Words wrapped in single asterisks are highlighted: "Write *better* hooks".
// An asterisk without a partner is kept as a plain character.
export const parseRuns = (text: string): Array<TextRun> => {
    const runs: Array<TextRun> = []
    const parts = text.split('*')
    const pairs = parts.length - 1
    const balanced = pairs % 2 === 0 ? pairs : pairs - 1

    parts.forEach((part, index) => {
        const highlight = index % 2 === 1 && index <= balanced
        const literalStar = index > 0 && index > balanced ? '*' : ''
        const value = literalStar + part

        if (!value) return

        const last = runs[runs.length - 1]

        if (last && last.highlight === highlight) last.text += value
        else runs.push({ text: value, highlight })
    })

    return runs
}

export const stripMarks = (text: string): string => parseRuns(text).map(run => run.text).join('')

export const displayText = (element: Pick<TextElement, 'uppercase'>, text: string): string => element.uppercase ? text.toLocaleUpperCase() : text

type Token = {
    text: string
    highlight: boolean
    space: boolean
}

const tokenize = (runs: Array<TextRun>): Array<Token> => {
    const tokens: Array<Token> = []

    for (const run of runs) {
        for (const piece of run.text.split(/(\s+)/)) {
            if (!piece) continue

            tokens.push({ text: piece, highlight: run.highlight, space: /^\s+$/.test(piece) })
        }
    }

    return tokens
}

const pushToken = (line: LaidLine, token: Token, width: number): void => {
    const last = line.runs[line.runs.length - 1]

    if (last && last.highlight === token.highlight) {
        last.text += token.text
        last.width += width
    }
    else {
        line.runs.push({ text: token.text, highlight: token.highlight, width })
    }

    line.width += width
}

const trimLine = (line: LaidLine, measureRun: (text: string) => number): LaidLine => {
    const last = line.runs[line.runs.length - 1]

    if (last && /\s+$/.test(last.text)) {
        const trimmed = last.text.replace(/\s+$/, '')
        const width = measureRun(trimmed)

        line.width -= last.width - width
        last.text = trimmed
        last.width = width

        if (!trimmed) line.runs.pop()
    }

    return line
}

export const layoutText = (element: TextElement, text: string, measure: TextMeasure): TextLayout => {
    const font = fontCss(element.font, element.size, element.weight, element.italic)
    const measureRun = (value: string): number => measure(value, font, element.letterSpacing)
    const lineHeight = element.size * element.lineHeight
    const lines: Array<LaidLine> = []
    const shown = displayText(element, text)
    const maxWidth = Math.max(element.width, element.size)

    for (const paragraph of shown.split('\n')) {
        const tokens = tokenize(parseRuns(paragraph))
        let line: LaidLine = { runs: [], width: 0 }

        if (!tokens.length) {
            lines.push(line)
            continue
        }

        for (const token of tokens) {
            if (token.space && !line.runs.length) continue

            const width = measureRun(token.text)

            if (!token.space && line.width + width > maxWidth && line.runs.length) {
                lines.push(trimLine(line, measureRun))
                line = { runs: [], width: 0 }
            }

            if (!token.space && width > maxWidth) {
                // A single word wider than the box is broken by characters.
                let chunk = ''

                for (const char of token.text) {
                    const next = chunk + char

                    if (measureRun(next) > maxWidth && chunk) {
                        pushToken(line, { ...token, text: chunk }, measureRun(chunk))
                        lines.push(line)
                        line = { runs: [], width: 0 }
                        chunk = char
                    }
                    else {
                        chunk = next
                    }
                }

                if (chunk) pushToken(line, { ...token, text: chunk }, measureRun(chunk))
                continue
            }

            pushToken(line, token, width)
        }

        lines.push(trimLine(line, measureRun))
    }

    return { lines, lineHeight, height: Math.max(lines.length, 1) * lineHeight }
}

// Shrinks the font size until the text fits its box, never below minSize.
export const fitTextSize = (element: TextElement, text: string, measure: TextMeasure, startSize: number): number => {
    if (!element.maxHeight) return element.size

    const minSize = element.minSize ?? Math.round(startSize * 0.45)
    let size = startSize

    while (size > minSize) {
        const height = layoutText({ ...element, size }, text, measure).height

        if (height <= element.maxHeight) return size

        size -= Math.max(1, Math.round(size * 0.04))
    }

    return minSize
}

// Measuring without a browser, used for server rendered previews. It slightly
// over-estimates widths so previews wrap a little early rather than overflow.
export const estimateMeasure: TextMeasure = (text, font, letterSpacing) => {
    const sizeMatch = font.match(/(\d+(?:\.\d+)?)px/)
    const size = sizeMatch ? Number(sizeMatch[1]) : 16
    const family = FONT_FACTOR_LOOKUP.find(([needle]) => font.includes(needle))
    const factor = family ? family[1] : 0.56
    let units = 0

    for (const char of text) {
        if (char === ' ') units += 0.28
        else if ('il.,:;\'!|'.includes(char)) units += 0.32
        else if ('mwMW@'.includes(char)) units += 1.35
        else if (char >= 'A' && char <= 'Z') units += 1.18
        else units += 1
    }

    return units * size * factor + letterSpacing * text.length
}

const FONT_FACTOR_LOOKUP: Array<[string, number]> = []

export const registerEstimateFactors = (): void => {
    if (FONT_FACTOR_LOOKUP.length) return

    for (const id of ['inter', 'figtree', 'montserrat', 'space-grotesk', 'bricolage', 'archivo-black', 'anton', 'bebas', 'dm-serif', 'playfair', 'fraunces', 'caveat', 'jetbrains']) {
        const font = getFont(id)

        FONT_FACTOR_LOOKUP.push([font.primary, font.widthFactor])
    }
}

registerEstimateFactors()

let measureContext: CanvasRenderingContext2D | null = null
const measureCache = new Map<string, number>()

export const supportsLetterSpacing = (): boolean => typeof CanvasRenderingContext2D !== 'undefined' && 'letterSpacing' in CanvasRenderingContext2D.prototype

// Real measuring on a canvas, used by the designer and every export.
export const canvasMeasure: TextMeasure = (text, font, letterSpacing) => {
    if (typeof document === 'undefined') return estimateMeasure(text, font, letterSpacing)

    const cacheKey = `${font}|${letterSpacing}|${text}`
    const cached = measureCache.get(cacheKey)

    if (cached !== undefined) return cached

    if (!measureContext) measureContext = document.createElement('canvas').getContext('2d')

    if (!measureContext) return estimateMeasure(text, font, letterSpacing)

    measureContext.font = font

    let width: number

    if (supportsLetterSpacing()) {
        (measureContext as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = `${letterSpacing}px`
        width = measureContext.measureText(text).width
    }
    else {
        width = measureContext.measureText(text).width + letterSpacing * Array.from(text).length
    }

    if (measureCache.size > 6000) measureCache.clear()

    measureCache.set(cacheKey, width)

    return width
}

// Forget cached widths once a new face finishes loading.
export const clearMeasureCache = (): void => {
    measureCache.clear()
}

export const textFontWeight = (element: TextElement): number => nearestWeight(element.font, element.weight)
